/**
 * Kontoauszug Upload – eigenständige Tab-Seite (Buchhaltung)
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { Upload, CheckCircle, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTabs } from '@/contexts/TabContext';
import { emitDataRefresh } from '@/lib/tabEvents';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function KontoauszugUploadPage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const konten = useMemo(() => params.konten || [], [params.konten]);

  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [kontoId, setKontoId] = useState(konten[0]?.id || '');
  const [monat, setMonat] = useState(new Date().toISOString().slice(0, 7));
  const fileRef = useRef(null);

  useEffect(() => { setKontoId(konten[0]?.id || ''); }, [konten]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Bitte Datei auswählen'); return; }
    if (!kontoId) { toast.error('Bitte Konto auswählen'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('datei', file);
      formData.append('konto_id', kontoId);
      formData.append('klient_id', params.klientId);
      formData.append('monat', monat);
      const res = await fetch(`${BACKEND_URL}/api/buchhaltung/kontoauszug-upload`, {
        method: 'POST', body: formData
      }).then(r => r.json());
      if (res.success) {
        setResult(res);
        toast.success(`${res.umsaetze_count} Umsätze importiert`);
      } else { toast.error(res.error || 'Fehler beim Verarbeiten'); }
    } catch { toast.error('Upload-Fehler'); }
    finally { setUploading(false); }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="kontoauszug-upload-page">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Upload className="w-5 h-5 text-green-600" />Kontoauszug hochladen
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => closeTab(activeTab.id)}><X className="w-4 h-4" /></Button>
          </div>
          <CardDescription>PDF hochladen – OCR liest Umsätze automatisch aus</CardDescription>
        </CardHeader>
        <CardContent>
          {!result ? (
            <div className="space-y-4">
              <div>
                <Label>Konto</Label>
                <Select value={kontoId} onValueChange={setKontoId}>
                  <SelectTrigger><SelectValue placeholder="Konto wählen..." /></SelectTrigger>
                  <SelectContent>
                    {konten.map(k => <SelectItem key={k.id} value={k.id}>{k.bezeichnung} — {k.bank}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Monat</Label>
                <Input type="month" value={monat} onChange={e => setMonat(e.target.value)} />
              </div>
              <div>
                <Label>Kontoauszug (PDF)</Label>
                <input ref={fileRef} type="file" accept=".pdf"
                  className="mt-1 block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => closeTab(activeTab.id)}>Abbrechen</Button>
                <Button onClick={handleUpload} disabled={uploading} data-testid="upload-kontoauszug">
                  {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Wird verarbeitet...</> : <><Upload className="w-4 h-4 mr-2" />Hochladen & Auslesen</>}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="font-medium text-green-800 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" /> {result.umsaetze_count} Umsätze erkannt und importiert
                </p>
              </div>
              {result.ocr_vorschau && (
                <div>
                  <Label className="text-xs text-slate-500">OCR-Vorschau:</Label>
                  <div className="bg-slate-50 rounded-lg p-3 text-xs font-mono max-h-32 overflow-y-auto whitespace-pre-wrap">{result.ocr_vorschau}</div>
                </div>
              )}
              <div className="flex justify-end pt-2 border-t">
                <Button onClick={() => { emitDataRefresh('buchhaltung'); closeTab(activeTab.id); }} data-testid="finish-upload">Fertig</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
